// Typed JSON discovery, typeDef lowering, and TypeScript alias emission
// FEATURE: zen typed JSON column support for code generation
import { toCamelCase } from "drizzle-orm/casing";
import {
  type InterfaceDeclaration,
  ModuleKind,
  ModuleResolutionKind,
  Project,
  ScriptTarget,
  type SourceFile,
  SyntaxKind,
  type Type,
  type TypeAliasDeclaration,
} from "ts-morph";
import { mapColumn } from "./columns";
import type {
  TypeDefField,
  TypeDefInfo,
  TypeDefMapping,
  TypedJsonArtifacts,
  TypedJsonFieldInfo,
} from "./typed-json.types";
import type { EnumInfo, TableInfo, ViewInfo } from "./types";
import { emitGeneratedFileBanner, esc, toPascalCase } from "./utils";

type ExportedTypeDeclaration = TypeAliasDeclaration | InterfaceDeclaration;

interface ExportedTypeRegistry {
  get(name: string): Type | undefined;
  has(name: string): boolean;
}

function typeIncludesNull(type: Type): boolean {
  if (type.isNull()) return true;
  if (!type.isUnion()) return false;
  return type.getUnionTypes().some((member) => member.isNull());
}

function unwrapNullableType(type: Type): Type {
  if (!type.isUnion()) return type;

  const nonNull = type
    .getUnionTypes()
    .filter((member) => !member.isNull() && !member.isUndefined());
  if (nonNull.length !== 1) return type;

  return unwrapNullableType(nonNull[0]!);
}

function getResolvedArrayInfo(type: Type): { resolvedType: Type; isArray: boolean } {
  const resolvedType = unwrapNullableType(type);
  if (!resolvedType.isArray()) {
    return { resolvedType, isArray: false };
  }

  return {
    resolvedType: resolvedType.getArrayElementTypeOrThrow(),
    isArray: true,
  };
}

function normalizeTypeText(typeText: string): { baseText: string; isArray: boolean } {
  let normalized = typeText.replace(/\s+/g, " ").trim();
  normalized = normalized.replace(/\s*\|\s*(?:null|undefined)\s*$/g, "").trim();

  let isArray = false;
  if (normalized.endsWith("[]")) {
    normalized = normalized.slice(0, -2).trim();
    isArray = true;
  }

  if (normalized.startsWith("(") && normalized.endsWith(")")) {
    normalized = normalized.slice(1, -1).trim();
  }

  return { baseText: normalized, isArray };
}

export function buildTypeDefs(
  typesPath: string,
  tables: TableInfo[],
  views?: ViewInfo[],
  enumMap?: Map<string, EnumInfo>,
): TypedJsonArtifacts {
  const project = createTypedJsonProject(typesPath, !!views?.length);
  const sf = project.getSourceFileOrThrow(typesPath);
  const typeDefs = new Map<string, TypeDefInfo>();
  const fieldToTypeDef = new Map<string, TypeDefMapping>();
  const typedJsonFieldMap = new Map<string, TypedJsonFieldInfo>();
  const exportedTypes = createExportedTypeRegistry(sf);

  const entityBySqlName = new Map<string, { sqlToJsKey: Map<string, string>; typeName: string }>();
  for (const table of tables) entityBySqlName.set(table.sqlName, table);
  for (const view of views || []) entityBySqlName.set(view.sqlName, view);
  const tableByExportName = new Map(tables.map((table) => [table.exportName, table]));

  const sourceFiles = ["supabase/schema/tables.ts"];
  if (views?.length) sourceFiles.push("supabase/schema/views.ts");

  for (const filePath of sourceFiles) {
    scanFileForTypedColumns({
      sourceFile: project.getSourceFileOrThrow(filePath),
      exportedTypes,
      typeDefs,
      fieldToTypeDef,
      typedJsonFieldMap,
      entityBySqlName,
      tableByExportName,
      enumMap: enumMap || new Map(),
    });
  }

  return {
    typeDefs,
    fieldToTypeDef,
    typedJsonFields: sortTypedJsonFields([...typedJsonFieldMap.values()]),
  };
}

function createExportedTypeRegistry(sourceFile: SourceFile): ExportedTypeRegistry {
  const declarations = new Map<string, ExportedTypeDeclaration>();
  const resolvedTypes = new Map<string, Type>();

  for (const stmt of sourceFile.getStatements()) {
    if (stmt.getKind() === SyntaxKind.TypeAliasDeclaration) {
      const typeAlias = stmt.asKindOrThrow(SyntaxKind.TypeAliasDeclaration);
      if (typeAlias.isExported()) {
        declarations.set(typeAlias.getName(), typeAlias);
      }
    }

    if (stmt.getKind() === SyntaxKind.InterfaceDeclaration) {
      const iface = stmt.asKindOrThrow(SyntaxKind.InterfaceDeclaration);
      if (iface.isExported()) {
        declarations.set(iface.getName(), iface);
      }
    }
  }

  return {
    has(name) {
      return declarations.has(name);
    },
    get(name) {
      if (!declarations.has(name)) return undefined;
      const cachedType = resolvedTypes.get(name);
      if (cachedType) return cachedType;

      const declaration = declarations.get(name);
      if (!declaration) return undefined;

      const resolvedType = declaration.getType();
      resolvedTypes.set(name, resolvedType);
      return resolvedType;
    },
  };
}

function createTypedJsonProject(typesPath: string, includeViews: boolean): Project {
  const project = new Project({
    skipAddingFilesFromTsConfig: true,
    compilerOptions: {
      module: ModuleKind.ESNext,
      moduleResolution: ModuleResolutionKind.Bundler,
      target: ScriptTarget.ES2022,
      // Required so `T | null` survives type resolution; without it TypeScript erases `null`
      // from resolved types and every nullable JSON field would look non-nullable.
      strictNullChecks: true,
      allowJs: false,
      baseUrl: ".",
      paths: {
        "@/*": ["src/*"],
        "~/*": ["app/*"],
      },
    },
  });

  project.addSourceFileAtPath(typesPath);
  project.addSourceFileAtPath("supabase/schema/tables.ts");
  if (includeViews) {
    project.addSourceFileAtPath("supabase/schema/views.ts");
  }

  return project;
}

function scanFileForTypedColumns({
  sourceFile,
  exportedTypes,
  typeDefs,
  fieldToTypeDef,
  typedJsonFieldMap,
  entityBySqlName,
  tableByExportName,
  enumMap,
}: {
  sourceFile: SourceFile;
  exportedTypes: ExportedTypeRegistry;
  typeDefs: Map<string, TypeDefInfo>;
  fieldToTypeDef: Map<string, TypeDefMapping>;
  typedJsonFieldMap: Map<string, TypedJsonFieldInfo>;
  entityBySqlName: Map<string, { sqlToJsKey: Map<string, string>; typeName: string }>;
  tableByExportName: Map<string, TableInfo>;
  enumMap: Map<string, EnumInfo>;
}) {
  const registerTypedJsonField = (
    entitySqlName: string,
    columnSqlName: string,
    typeDefName: string,
    isArray: boolean,
    sourceTypeName?: string,
  ) => {
    const entity = entityBySqlName.get(entitySqlName);
    if (!entity) return;

    const fieldName = entity.sqlToJsKey.get(columnSqlName) || columnSqlName;
    const fieldKey = `${entitySqlName}.${fieldName}`;
    fieldToTypeDef.set(fieldKey, { typeDefName, isArray });
    typedJsonFieldMap.set(fieldKey, {
      entitySqlName,
      entityTypeName: entity.typeName,
      fieldName,
      typeDefName,
      isArray,
      sourceTypeName,
    });
  };

  for (const access of sourceFile.getDescendantsOfKind(SyntaxKind.PropertyAccessExpression)) {
    if (access.getName() !== "$type") continue;

    const call = access.getParentIfKind(SyntaxKind.CallExpression);
    if (!call || call.getExpression() !== access) continue;

    const typeArg = call.getTypeArguments()[0];
    if (!typeArg) continue;

    const propertyAssignment = call.getFirstAncestorByKind(SyntaxKind.PropertyAssignment);
    if (!propertyAssignment) continue;

    const columnSqlName = propertyAssignment.getName();
    const entitySqlName = getEntitySqlName(call);
    if (!entitySqlName) continue;

    const rawTypeText = typeArg.getText().trim();
    const { baseText, isArray: isArrayFromText } = normalizeTypeText(rawTypeText);

    const rowTypeDefName = resolveWholeRowTypeDefName(
      baseText,
      tableByExportName,
      typeDefs,
      enumMap,
    );
    if (rowTypeDefName) {
      const isArray = isArrayFromText || getResolvedArrayInfo(typeArg.getType()).isArray;
      registerTypedJsonField(entitySqlName, columnSqlName, rowTypeDefName, isArray);
      continue;
    }

    if (exportedTypes.has(baseText)) {
      const type = exportedTypes.get(baseText);
      if (type && tryBuildTypeDef(baseText, type, typeDefs, exportedTypes)) {
        const isArray = isArrayFromText || getResolvedArrayInfo(type).isArray;
        registerTypedJsonField(entitySqlName, columnSqlName, baseText, isArray, baseText);
      }
      continue;
    }

    const { resolvedType, isArray: isArrayFromType } = getResolvedArrayInfo(typeArg.getType());
    const isArray = isArrayFromType || isArrayFromText;
    const generatedName = `${toPascalCase(entitySqlName)}${toPascalCase(columnSqlName)}`;
    if (tryBuildTypeDef(generatedName, resolvedType, typeDefs, exportedTypes)) {
      registerTypedJsonField(entitySqlName, columnSqlName, generatedName, isArray);
    }
  }
}

function getEntitySqlName(call: import("ts-morph").CallExpression): string | null {
  let node = call.getParent();
  while (node) {
    if (node.getKind() === SyntaxKind.CallExpression) {
      const candidate = node.asKindOrThrow(SyntaxKind.CallExpression);
      const callee = candidate.getExpression().getText();
      if (callee === "pgTable" || callee === "pgView") {
        const firstArg = candidate.getArguments()[0];
        return firstArg ? firstArg.getText().replace(/['"]/g, "") : null;
      }
    }

    node = node.getParent();
  }

  return null;
}

function resolveWholeRowTypeDefName(
  typeText: string,
  tableByExportName: Map<string, TableInfo>,
  typeDefs: Map<string, TypeDefInfo>,
  enumMap: Map<string, EnumInfo>,
): string | null {
  const wholeRowMatch = typeText.match(/^typeof\s+(?:t\.)?(\w+)\.\$infer(?:Select|Insert)$/);
  if (!wholeRowMatch) return null;

  const refTable = tableByExportName.get(wholeRowMatch[1]!);
  if (!refTable) return null;

  const rowTypeDefName = `${refTable.typeName}Row`;
  if (!typeDefs.has(rowTypeDefName)) {
    buildRowTypeDef(rowTypeDefName, refTable, typeDefs, enumMap);
  }

  return typeDefs.has(rowTypeDefName) ? rowTypeDefName : null;
}

function buildRowTypeDef(
  name: string,
  table: TableInfo,
  typeDefs: Map<string, TypeDefInfo>,
  enumMap: Map<string, EnumInfo>,
) {
  const fields: Record<string, TypeDefField> = {};

  for (const jsKey of [...table.sqlToJsKey.values()]) {
    const col = (table.table as any)[jsKey];
    if (!col?.columnType) continue;

    const mapped = mapColumn(jsKey, col, enumMap);
    if (mapped.isGenerated) continue;
    if (mapped.sqlName === "$hash") continue;

    let type: string;
    switch (mapped.type) {
      case "String":
      case "Int":
      case "Float":
      case "Boolean":
      case "DateTime":
      case "Json":
        type = mapped.type;
        break;
      case "BigInt":
        type = "Int";
        break;
      case "Decimal":
        type = "Float";
        break;
      default:
        type = "String";
        break;
    }

    // Emit a camelCase field name and record the original key as `rawName`, which
    // drives an `@map` attribute. The ORM applies that `@map` when (de)serializing
    // and filtering JSON content, so app code uses camelCase uniformly while storage
    // keeps its original (possibly snake_case) keys.
    const fieldName = toCamelCase(mapped.name);
    fields[fieldName] = {
      name: fieldName,
      ...(fieldName !== mapped.name && { rawName: mapped.name }),
      type,
      ...(mapped.optional && { optional: true }),
      ...(mapped.array && { array: true }),
    };
  }

  if (Object.keys(fields).length > 0) {
    typeDefs.set(name, { name, fields });
  }
}

export function tryBuildTypeDef(
  name: string,
  type: Type,
  typeDefs: Map<string, TypeDefInfo>,
  exportedTypes: ExportedTypeRegistry,
): boolean {
  if (typeDefs.has(name)) return true;

  if (type.isArray()) {
    return tryBuildTypeDef(name, type.getArrayElementTypeOrThrow(), typeDefs, exportedTypes);
  }

  if (type.isUnion()) {
    const nonNull = type
      .getUnionTypes()
      .filter((member) => !member.isNull() && !member.isUndefined());
    if (nonNull.length === 1) {
      return tryBuildTypeDef(name, nonNull[0]!, typeDefs, exportedTypes);
    }

    return false;
  }

  if (!type.isObject() || type.getProperties().length === 0) return false;
  if (type.getStringIndexType()) return false;
  if (type.getCallSignatures().length > 0) return false;

  const fields: Record<string, TypeDefField> = {};

  for (const prop of type.getProperties()) {
    const propName = prop.getName();
    const declaration = prop.getValueDeclaration();
    if (!declaration) continue;

    const propType = prop.getTypeAtLocation(declaration);
    // camelCase the field name; `rawName` preserves the original key for @map (see
    // buildRowTypeDef). The ORM applies @map to JSON content, so app code is uniform
    // camelCase over storage that may use snake_case keys.
    const fieldName = toCamelCase(propName);
    const mappedField = mapTypeToTypeDef(propName, propType, name, typeDefs, exportedTypes);

    fields[fieldName] = {
      name: fieldName,
      ...(fieldName !== propName && { rawName: propName }),
      type: mappedField?.type || "Json",
      ...(prop.isOptional() && { optional: true }),
      // A `T | null` prop keeps its nullability so the emitted JSON shape restores `| null`
      // (distinct from an optional `?:` key). `mapTypeToTypeDef` strips the null to resolve the
      // base type, so detect it here from the original prop type.
      ...(typeIncludesNull(propType) && { nullable: true }),
      ...(mappedField?.isArray && { array: true }),
    };
  }

  if (Object.keys(fields).length === 0) return false;

  typeDefs.set(name, { name, fields });
  return true;
}

function mapTypeToTypeDef(
  fieldName: string,
  type: Type,
  parentName: string,
  typeDefs: Map<string, TypeDefInfo>,
  exportedTypes: ExportedTypeRegistry,
): { type: string; isArray: boolean } | null {
  if (type.isUnion()) {
    const unwrapped = unwrapNullableType(type);
    if (unwrapped !== type) {
      return mapTypeToTypeDef(fieldName, unwrapped, parentName, typeDefs, exportedTypes);
    }
    const nonNull = type
      .getUnionTypes()
      .filter((member) => !member.isNull() && !member.isUndefined());
    if (nonNull.every((member) => member.isStringLiteral() || member.isString())) {
      return { type: "String", isArray: false };
    }
    if (nonNull.every((member) => member.isNumberLiteral() || member.isNumber())) {
      return { type: "Float", isArray: false };
    }
    if (nonNull.every((member) => member.isBooleanLiteral() || member.isBoolean())) {
      return { type: "Boolean", isArray: false };
    }
    return null;
  }

  if (type.isString() || type.isStringLiteral()) return { type: "String", isArray: false };
  if (type.isNumber() || type.isNumberLiteral()) return { type: "Float", isArray: false };
  if (type.isBoolean() || type.isBooleanLiteral()) return { type: "Boolean", isArray: false };
  if (type.getSymbol()?.getName() === "Date") return { type: "DateTime", isArray: false };

  if (type.isArray()) {
    const inner = mapTypeToTypeDef(
      fieldName,
      type.getArrayElementTypeOrThrow(),
      parentName,
      typeDefs,
      exportedTypes,
    );
    return inner ? { type: inner.type, isArray: true } : null;
  }

  if (type.isObject() && type.getProperties().length > 0) {
    if (type.getStringIndexType()) return { type: "Json", isArray: false };
    if (type.getCallSignatures().length > 0) return null;

    const symbol = type.getAliasSymbol() || type.getSymbol();
    const knownName = symbol?.getName();
    if (knownName && exportedTypes.has(knownName)) {
      if (tryBuildTypeDef(knownName, type, typeDefs, exportedTypes)) {
        return { type: knownName, isArray: false };
      }
    }

    const subTypeDefName = `${parentName}${toPascalCase(fieldName)}`;
    if (tryBuildTypeDef(subTypeDefName, type, typeDefs, exportedTypes)) {
      return { type: subTypeDefName, isArray: false };
    }
  }

  return null;
}

export function emitTypeDefsBlock(typeDefs: Map<string, TypeDefInfo>): string {
  if (typeDefs.size === 0) return "";

  const lines: string[] = [];
  lines.push("    typeDefs = {");

  for (const [name, typeDef] of sortTypeDefs(typeDefs)) {
    lines.push(`        ${name}: {`);
    lines.push(`            name: ${esc(typeDef.name)},`);
    lines.push(`            fields: {`);

    for (const field of Object.values(typeDef.fields)) {
      const parts = [`name: ${esc(field.name)}`, `type: ${esc(field.type)}`];
      // The ORM has no dedicated nullable flag — `optional: true` is what makes its derived CRUD
      // type `T | null` (see WrapType). So a nullable field (even one whose key is required) must
      // be emitted as optional for the ORM to accept `null`.
      if (field.optional || field.nullable) parts.push("optional: true");
      if (field.array) parts.push("array: true");
      if (field.rawName && field.rawName !== field.name) {
        parts.push(
          `attributes: [{ name: "@map", args: [{ name: "name", value: ExpressionUtils.literal(${esc(field.rawName)}) }] }]`,
        );
      }
      lines.push(`                ${field.name}: { ${parts.join(", ")} },`);
    }

    lines.push("            }");
    lines.push("        },");
  }

  lines.push("    } as const;");
  return lines.join("\n");
}

function mapTypeDefFieldToTs(
  field: Pick<TypeDefField, "type" | "array">,
  typeDefs: Map<string, TypeDefInfo>,
): string {
  let tsType: string;

  switch (field.type) {
    case "String":
      tsType = "string";
      break;
    case "Int":
    case "Float":
      tsType = "number";
      break;
    case "Boolean":
      tsType = "boolean";
      break;
    case "DateTime":
      tsType = "Date";
      break;
    case "Json":
      tsType = "unknown";
      break;
    default:
      tsType = typeDefs.has(field.type) ? `${field.type}JsonShape` : "unknown";
      break;
  }

  return field.array ? `${tsType}[]` : tsType;
}

function getJsonFieldAlias(info: TypedJsonFieldInfo): string {
  return `${info.entityTypeName}${toPascalCase(info.fieldName)}Json`;
}

function getJsonRawFieldAlias(info: TypedJsonFieldInfo): string {
  return `${getJsonFieldAlias(info)}Raw`;
}

export function emitJsonNamespaceBlock(
  typeDefs: Map<string, TypeDefInfo>,
  typedJsonFields: TypedJsonFieldInfo[],
  indent = "",
): string[] {
  const out: string[] = [];

  for (const [name, typeDef] of sortTypeDefs(typeDefs)) {
    out.push(`${indent}export type ${name}JsonShape = {`);
    for (const field of Object.values(typeDef.fields)) {
      const optional = field.optional ? "?" : "";
      const nullSuffix = field.nullable ? " | null" : "";
      out.push(
        `${indent}  ${field.name}${optional}: ${mapTypeDefFieldToTs(field, typeDefs)}${nullSuffix};`,
      );
    }
    out.push(`${indent}};`);
    out.push("");
  }

  for (const info of typedJsonFields) {
    out.push(`${indent}export type ${getJsonFieldAlias(info)} = ${info.typeDefName}JsonShape;`);
    out.push("");
  }

  if (out.at(-1) === "") {
    out.pop();
  }

  return out;
}

export function emitJsonRawNamespaceBlock(
  typedJsonFields: TypedJsonFieldInfo[],
  indent = "",
): string[] {
  const out: string[] = [];

  for (const info of typedJsonFields.filter((field) => field.sourceTypeName)) {
    out.push(
      `${indent}export type ${getJsonRawFieldAlias(info)} = RawJson.${info.sourceTypeName};`,
    );
    out.push("");
  }

  if (out.at(-1) === "") {
    out.pop();
  }

  return out;
}

export function emitJsonTs(
  typeDefs: Map<string, TypeDefInfo>,
  typedJsonFields: TypedJsonFieldInfo[],
): string {
  const out: string[] = [...emitGeneratedFileBanner(), ""];
  out.push(...emitJsonNamespaceBlock(typeDefs, typedJsonFields));
  out.push("");

  return out.join("\n");
}

export function emitJsonRawTs(typedJsonFields: TypedJsonFieldInfo[]): string {
  const out: string[] = [
    ...emitGeneratedFileBanner(),
    "",
    'import type * as RawJson from "../../supabase/schema/types";',
    "",
  ];
  out.push(...emitJsonRawNamespaceBlock(typedJsonFields));
  out.push("");

  return out.join("\n");
}

function sortTypeDefs(typeDefs: Map<string, TypeDefInfo>): [string, TypeDefInfo][] {
  return [...typeDefs.entries()].sort(([left], [right]) => left.localeCompare(right));
}

function sortTypedJsonFields(typedJsonFields: TypedJsonFieldInfo[]): TypedJsonFieldInfo[] {
  return [...typedJsonFields].sort((left, right) => {
    const leftKey = `${getJsonFieldAlias(left)}:${left.sourceTypeName || ""}`;
    const rightKey = `${getJsonFieldAlias(right)}:${right.sourceTypeName || ""}`;
    return leftKey.localeCompare(rightKey);
  });
}
